<script lang="ts">
	// export let name;
	import ColorFamily from './blocks/color-family.svelte';
	import Button from './components/button.svelte';
	import Code from './components/code.svelte';
	import InputCheckbox from './components/input-checkbox.svelte';
	import InputNumber from './components/input-number.svelte';
	import InputRadio from './components/input-radio.svelte';
	import Input from './components/input.svelte';
	import { familyNames, lightnessParamsS, familiesS, prefs, appState, windowWidth } from './stores';
	import { hexToHSL, hslToRGB, rgbToHex, newContrastCheckerFunction, hexToRGB, rgbToHSL, getLuminance, hctToRGB, hexToHCT } from './utilities/color-conversion.js';
	import writeExportCode from './utilities/export-utilities';
	import {getSwatchLightness} from './utilities/stepping-methods';
	import Select from './components/select.svelte';
	import Graph from './components/graph.svelte';
	import TabIcon from './components/tab-icon.svelte';
	import FamilyControls from './blocks/family-controls.svelte';
	import FullView from './blocks/full-view.svelte';
	import BrandButton from './blocks/brand-button.svelte';
	import Modal from './blocks/modal.svelte';
    import NavMenu from './blocks/nav-menu.svelte';
    import Backdrop from './components/backdrop.svelte';
    import { slide } from 'svelte/transition';
    import NotificationManager from './blocks/notification-manager.svelte';
    import Callout from './components/callout.svelte';
    import HorizontalRule from './components/horizontal-rule.svelte';
    import Icons from './components/icons.svelte';

	// run all the tests
	// colorConversionTest();
	let graphData = [];
	$: exportCode = writeExportCode($familiesS, $lightnessParamsS, $prefs);

	// Check local storage
	function checkLocalStorage() {
		let savedColors = localStorage.getItem('colors');
		if (!savedColors) {
			// set the theme
			document.documentElement.setAttribute('data-theme', 'System');

			// if null start from scratch
			updateAllFamilies();
			updateSavedColors();
		} else {
			// load saved colors
			let savedColorsObj = JSON.parse(savedColors);
			$familiesS = savedColorsObj.families;

			// load lightness parameters
			$lightnessParamsS.max = savedColorsObj.lightnessParams.max;
			$lightnessParamsS.min = savedColorsObj.lightnessParams.min;
			$lightnessParamsS.steps = savedColorsObj.lightnessParams.steps;
			// people might have saved data, but not a method here.
			$lightnessParamsS.method = savedColorsObj.lightnessParams.method ? savedColorsObj.lightnessParams.method : 'Linear';

			// load prefs
			if(savedColorsObj.prefs) {
				Object.keys(savedColorsObj.prefs).forEach(function(key,index) {
					$prefs[key] = savedColorsObj.prefs[key];
				});
				
				// check for theme prefs
				document.documentElement.setAttribute('data-theme', $prefs.theme);
			}
			updateAllFamilies();
		}
	}
	checkLocalStorage();

	function updateSavedColors() {
		let storageObject = {
			families: $familiesS,
			lightnessParams: $lightnessParamsS,
			prefs: $prefs
		}
		let storageString = JSON.stringify(storageObject);
		localStorage.setItem('colors', storageString);
	}

	type AppStateArea = 'global' | 'family' | 'view' | 'familySelectedIndex';
	function manageAppState(area:AppStateArea, state:any) {
		if (area === 'family' && state === '-1') {
			$appState.familySelected = false;
		} else if (area === 'family') {
			$appState.familySelected = true;
			$appState[area] = state;
		} else {
			$appState[area] = state;
		}
	}

	let addFamilyHex = '';

	function addFamily(hex:string) {
		let newHue:number;
		let newSaturation:number;
		let inputHSL:any;
		if (hex) {
			inputHSL = hexToHSL(hex);
		}

		if (hex && inputHSL !== undefined) {
			newHue = inputHSL.h;
			newSaturation = inputHSL.s;
		} else if ($familiesS.length === 0) {
			// total random hue with some saturation
			newHue = Math.round(Math.random() * 360);
			newSaturation = Math.round((Math.random() * 50) + 35);
		} else {
			// Get last family's hue and rotate 30
			newHue = $familiesS[$familiesS.length-1].hue - 30;
			if (newHue < 0) {
				newHue = 360 + newHue;
			}
			newSaturation = 67;
		}

		let startingName = getFamilyNameNew(newHue, newSaturation);

		let newFam = {
			id: crypto.randomUUID(), // change back to famCounter if it breaks
			name: startingName,
			hue: newHue,
			saturation: newSaturation,
			swatchGroup: [],
			anchor: hex,
			anchorPref: false,
			anchorValidity: false
		};
		familiesS.set([...$familiesS, newFam]);
		updateFamily(newFam.id, false, 'new');
		manageAppState('family', newFam.id);
		manageAppState('familySelectedIndex', $familiesS.length - 1);
	}

	function getFamilyNameNew(hue:number, saturation:number) {
		let familyName = $familyNames[$prefs.colorSpace][0].name;
		for (let i = 0; i < $familyNames[$prefs.colorSpace].length; i++) {
			if (hue >= $familyNames[$prefs.colorSpace][i].min && hue < $familyNames[$prefs.colorSpace][i].max) {
				familyName = $familyNames[$prefs.colorSpace][i].name
				break
			}
		}

		if (saturation === 0) {
			familyName = 'Gray';
		} else if (saturation < 10) {
			familyName = familyName + ' Gray';
		} else if (saturation < 25) {
			familyName = 'Grayish ' + familyName;
		} else if (saturation < 42) {
			familyName = 'Faded ' + familyName;
		}

		return familyName;
	}

	function getFamilyIndex(id:string) {
		let matchFound = false;
		for (let i = 0; i < $familiesS.length; i++) {
			if ($familiesS[i].id === id) {
				matchFound = true;
				return i;
			} 
		}
		if (matchFound === false) {
			return -1;
		}
	}

	export function deleteFamily(id:string) {
		let index = getFamilyIndex(id);
		$familiesS.splice(index, 1);
		$appState.familySelected = false;
	}

	const familyFunctions = {
		delete: deleteFamily,
		updateAll: updateAllFamilies,
		randomize: randomizeFamilyValues,
		update: updateFamily,
		move: moveFamily,
		newAnchor: tryNewHexCode,
		toggleAnchorState: toggleAnchorState,
		manageAppState: manageAppState,
		toggleModulation: toggleModulation,
		getFamilyIndex: getFamilyIndex
	}

	function tryNewHexCode(id:string, hex:string) {
		let index = getFamilyIndex(id);
		let rgbColor = hexToRGB(hex);
		if (rgbColor === undefined) {
			// an invalid hex code was submitted
			$familiesS[index].anchorValidity = false;
		} else {
			let famColor;
			if ($prefs.colorSpace === 'HSL') {
				famColor = rgbToHSL(rgbColor);
			} else if ($prefs.colorSpace === 'HCT') {
				const hctData = hexToHCT(hex);
				famColor = {h: hctData.hue, s:hctData.chroma};
			}
			$familiesS[index].hue = famColor.h;
			$familiesS[index].saturation = famColor.s;
			$familiesS[index].anchorValidity = true;
			updateFamily(id, false, 'anchor');
		}
	}

	function randomizeFamilyValues(id:string) {
		let index = getFamilyIndex(id);
		let hue = Math.round(Math.random() * 360);
		let saturation = Math.round((Math.random() * 50) + 35);
		let name = getFamilyNameNew(hue, saturation);
		$familiesS[index].hue = hue;
		$familiesS[index].saturation = saturation;
		$familiesS[index].name = name;
		$familiesS[index].anchor = '';
		updateFamily(id, false, 'randomize');
	}

	type FamChangeTypes = 'new' | 'name' | 'hue' | 'saturation' | 'randomize' | 'anchor' | 'modulation';
	function updateFamily(id:string, nameChange:boolean, changeType:FamChangeTypes) {
		let index = getFamilyIndex(id);
		
		if (changeType !== 'name') {
			updateSwatchGroup(id);
			$familiesS[index].name = getFamilyNameNew($familiesS[index].hue, $familiesS[index].saturation);
		}
		if (changeType === 'hue' || changeType === 'saturation') {
			// remove the anchor if user futzes with the hue or saturation
			$familiesS[index].anchor = '';
		}
		exportCode = writeExportCode($familiesS, $lightnessParamsS, $prefs);
		updateSavedColors();
	}

	function toggleAnchorState(id:string, hex:string) {
		// using this function to manage state instead of binding because the swatches would update before the reactive state was updated
		let index = getFamilyIndex(id);
		$familiesS[index].anchorPref = !$familiesS[index].anchorPref;
		if ($familiesS[index].anchorPref) {
			tryNewHexCode(id, hex);
		} else {
			updateFamily(id, false, 'anchor');
		}		
	}

	function updateAllFamilies() {
		$familiesS.forEach((family, index) => {
			updateSwatchGroup(family.id);
		});
		updateSavedColors();
	}

	function updateSwatchGroup(id:string) {
		let index = getFamilyIndex(id);
		let lightnessSpread = $lightnessParamsS.max - $lightnessParamsS.min;
		let stepAmount = lightnessSpread / (($lightnessParamsS.steps) - 1);
		let newSwatchGroup = [];
		let lightness:any, saturation:number, hue:number, origSaturation:number, origHue:number;
		let method = $lightnessParamsS.method.toString();
		let coords;
		graphData = [];
		for (let j = 0; j < $lightnessParamsS.steps; j++) {
			if (lightnessSpread === 0) {
				lightness = $lightnessParamsS.min;
			} else {
				lightness = getSwatchLightness($lightnessParamsS.steps, $lightnessParamsS.min, $lightnessParamsS.max, method, j);
			}

			coords = {x:lightness,y:((j/($lightnessParamsS.steps-1))*100)}
			graphData = [...graphData, coords];

			saturation = $familiesS[index].saturation;
			origSaturation = saturation;
			if ($familiesS[index].saturationShift) {
				saturation = getValueShift(j, $lightnessParamsS.steps, $familiesS[index].saturation, 100, ($familiesS[index].saturationShiftAmount / 2), false );
			}

			hue = $familiesS[index].hue;
			origHue = hue;
			if ($familiesS[index].hueShift) {
				hue = getValueShift(j, $lightnessParamsS.steps, $familiesS[index].hue, 360, ($familiesS[index].hueShiftAmount / 10), true );
			}

			let rgb;
			if ($prefs.colorSpace === 'HCT') {
				let hct = {hue: hue, chroma: saturation, tone: lightness}
				rgb = hctToRGB(hct);
			} else {
				rgb = hslToRGB(hue, saturation, lightness);
			}

			let luminance = getLuminance(rgb);
			let hex = rgbToHex(rgb);
			let textContrastData = newContrastCheckerFunction(luminance, $prefs.textColors, $prefs.swatchA11yLarge);
			let newSwatch = {
				hue: hue,
				saturation: saturation,
				lightness: lightness,
				luminance: luminance,
				red: rgb.r,
				green: rgb.g,
				blue: rgb.b,
				hex: hex,
				textContrastData: textContrastData,
				anchorIcon: ($familiesS[index].anchorPref && $familiesS[index].anchor === hex) ? true : false
			};
			newSwatchGroup = [...newSwatchGroup, newSwatch];
		}
		if ($familiesS[index].anchorPref && $familiesS[index].anchor) {
			let rgb = hexToRGB($familiesS[index].anchor);
			let luminance = getLuminance(rgb);
			let hsl:{h:number, s:number, l:number};
			if ($prefs.colorSpace === 'HCT') {
				const hct = hexToHCT($familiesS[index].anchor);
				hsl = {h: hct.hue, s: hct.chroma, l: hct.tone};
			} else {
				hsl = rgbToHSL(rgb);
			}
			let textContrastData = newContrastCheckerFunction(luminance, $prefs.textColors, $prefs.swatchA11yLarge);

			let anchorSwatch = {
				hue: origHue,
				saturation: origSaturation,
				lightness: hsl.l,
				luminance: luminance,
				red: rgb.r,
				green: rgb.g,
				blue: rgb.b,
				hex: $familiesS[index].anchor,
				textContrastData: textContrastData,
				anchorIcon: true
			};

			// find where to insert the anchor color based on it's own lightness
			let anchorPos:number, i:number;
			let anchorSwatchResolved = false;
			for (i = 0; i < (newSwatchGroup.length); i++) {
				if (newSwatchGroup[i].hex === anchorSwatch.hex) {
					anchorSwatchResolved = true;
					break;
				} else if (newSwatchGroup[i].lightness > hsl.l) {
					anchorSwatchResolved = true;
					anchorPos = i;
					newSwatchGroup.splice(anchorPos, 0, anchorSwatch);
					break;
				}
			}
			if (!anchorSwatchResolved) {
				newSwatchGroup.splice(newSwatchGroup.length, 0, anchorSwatch);
			}
		}
		$familiesS[index].swatchGroup = newSwatchGroup;
		exportCode = writeExportCode($familiesS, $lightnessParamsS, $prefs);
	}

	function moveFamily(moveUp:boolean, id:string) {
		let index = getFamilyIndex(id);

		//first remove from array
		let fam = $familiesS.splice(index, 1);
		if (moveUp) {
			index--
		} else {
			index++
		}
		// then add it back in the right spot
		$familiesS.splice(index, 0, fam[0]);

		manageAppState('familySelectedIndex', index);
		updateAllFamilies();
	}

	function getValueShift(step:number, steps:number, origVal:number, rangeMax:number, amount:number, looping:boolean) {
		const midStep = (steps - 1) / 2;
		const maxShift = amount * rangeMax;
		const shiftStrength = Math.abs((midStep - step) / midStep);

		let shiftedVal = origVal;
		if (step < (midStep) || step > (midStep)) {
			shiftedVal = (maxShift * shiftStrength) + origVal;
		}
		
		if (shiftedVal > rangeMax && !looping) {
			shiftedVal = rangeMax;
		} else if (shiftedVal > rangeMax) {
			shiftedVal = shiftedVal - rangeMax;
		}

		return shiftedVal;
	}

	function toggleModulation(id:string, modType:string) {
		// using this function to manage state instead of binding because the swatches would update before the reactive state was updated
		
		// find the index of the correct family
		let index = getFamilyIndex(id);
		
		$familiesS[index][modType] = !$familiesS[index][modType];
		// check for amount
		let propertyName = modType + 'Amount'
		if (!$familiesS[index][propertyName]) {
			$familiesS[index][propertyName] = 0;
		}
		updateFamily(id, false, 'modulation');
	}

	const a11yValidityWarning:string = 'Invalid hex code';
	function updateA11yThemePrefs(id:number) {
		let color = hexToRGB($prefs.textColors[id].hex);
		if (color !== undefined) {
			$prefs.textColors[id].rgb = color;
			$prefs.textColors[id].valid = true;
		} else {
			$prefs.textColors[id].rgb = color;
			$prefs.textColors[id].valid = false;
		}
		updateAllFamilies();
	}

	function togglePrefs(pref:any) {
		$prefs[pref] = !$prefs[pref];
		updateSavedColors();
	}

	function updateA11yFontSize() {
		togglePrefs('swatchA11yLarge');
		updateAllFamilies();
	}

	type prefType = 'exportColorFormat' | 'exportAccessibility' | 'exportCodeType' | 'swatchHex' | 'swatchLabel' | 'swatchA11y' | 'swatchA11yLarge' | 'swatchSeperation' | 'swatchRounded' | 'textColors' | 'showOptions' | 'harmony' | 'theme' | 'colorSpace';
	function updatePrefs(pref:prefType, value) {
		$prefs[pref] = value;
		updateSavedColors();
	}

	function manageTheme(theme:string) {
		$prefs.theme = theme;
		document.documentElement.setAttribute('data-theme', $prefs.theme);
		updateSavedColors();
	}

	function exportDataToClipBoard(data:string) {
		navigator.clipboard.writeText(data);
	}

	function udpateColorSpace(space:string) {
		$prefs.colorSpace = space;
		$familiesS.forEach((family, index) => {
			family.name = getFamilyNameNew(family.hue, family.saturation);
			if (space === 'HSL' && family.saturation > 100) {
				family.saturation = 100;
			}
			if (family.anchorPref) {
				tryNewHexCode(family.id, family.anchor);
			}
		});
		updateAllFamilies();
	}

	let lightnessLabel = 'Lightness'
	$: if ($prefs.colorSpace === 'HCT') {
		lightnessLabel = 'Tone';
	} else {
		lightnessLabel = 'Lightness';
	}

</script>

<svelte:window bind:innerWidth={$windowWidth} />
<svelte:head>
	<link rel="preconnect" href="https://rsms.me/">
	<link rel="stylesheet" href="https://rsms.me/inter/inter.css">
	<script defer data-domain="color-pal.com" src="https://plausible.io/js/script.js"></script>
</svelte:head>

<header>
	<div class="site-brand-area">
		<h1 class="title-text">Color Pal</h1>
		<BrandButton />
	</div>
	<Button label="" handleClick={() => manageAppState('view', 1)} role="tertiary" icon="menu" ariaLabel="menu" roundedCorners={false}/>
</header>

<NavMenu active={$appState.view === 1}></NavMenu>
<NotificationManager />

<main class:global-active="{$appState.global !== -1}" class:family-selected="{$appState.familySelected}">
	<section class="sm-order-1 global-settings-area" class:active="{$appState.global !== -1}">
		<div class="tab-area" role="tablist">
			<TabIcon label={lightnessLabel} icon={'sun'} selected={$appState.global === 0} handleClick={() => {manageAppState('global', 0)}} tabid={'global-tab-0'} />
			<TabIcon label={'Swatches'} icon={'swatches'} selected={$appState.global === 1} handleClick={() => {manageAppState('global', 1)}} tabid={'global-tab-1'} />
			<TabIcon label={'A11y'} icon={'human'} selected={$appState.global === 2} handleClick={() => {manageAppState('global', 2)}} tabid={'global-tab-2'} />
		</div>

		<div class="settings-area">
			<div class="info-area" class:active="{$appState.global === 0}" role="tabpanel" aria-labelledby="global-tab-0">
				<div class="info-header">
					<h2>{lightnessLabel}</h2>
					<Button label="" icon={'x'} role={'tertiary'} handleClick={() => {manageAppState('global', -1)}} ariaLabel="Hide lightness controls" roundedCorners={false} />
				</div>
				<div class="info-content">
					<InputNumber name={'lightness-min'} label={'min'} bind:value={$lightnessParamsS.min} on:message={updateAllFamilies} />
					<InputNumber name={'lightness-max'} label={'max'} bind:value={$lightnessParamsS.max} on:message={updateAllFamilies} />
					<InputNumber name={'lightness-steps'} label={'Steps'} bind:value={$lightnessParamsS.steps} min={1} max={32} on:message={updateAllFamilies} />
					<Select label="Intervals" name="step-method" options={['Linear', 'Ease in/out']} bind:value={$lightnessParamsS.method} on:message={updateAllFamilies} />
					<Graph graphData={graphData} />
				</div>
			</div>

			<div class="info-area" class:active="{$appState.global === 1}" role="tabpanel" aria-labelledby="global-tab-1">
				<div class="info-header">
					<h2>Swatches</h2>
					<Button label="" icon={'x'} role={'tertiary'} handleClick={() => {manageAppState('global', -1)}} ariaLabel="Hide swatches controls" roundedCorners={false} />
				</div>
				<div class="info-content">
					<InputCheckbox label="Show label" name={'Show label'} disabled={false} bind:checked={$prefs.swatchLabel} on:message={() => togglePrefs('swatchLabel')} />
					<InputCheckbox label="Show hex" name={'Show hex'} disabled={false} bind:checked={$prefs.swatchHex} on:message={() => togglePrefs('swatchHex')} />
					<InputCheckbox label="Separation" name={'Separation'} disabled={false} bind:checked={$prefs.swatchSeperation} on:message={() => togglePrefs('swatchSeperation')} />
					<InputCheckbox label="Rounded" name={'Rounded'} disabled={false} bind:checked={$prefs.swatchRounded} on:message={() => togglePrefs('swatchRounded')} />
				</div>
			</div>

			<div class="info-area" class:active="{$appState.global === 2}" role="tabpanel" aria-labelledby="global-tab-2">
				<div class="info-header">
					<h2>Accessibility</h2>
					<Button label="" icon={'x'} role={'tertiary'} handleClick={() => {manageAppState('global', -1)}} ariaLabel="Hide a11y controls" roundedCorners={false} />
				</div>
				<div class="info-content">
					<InputCheckbox label="Text accessibility" name={'Text accessibility'} disabled={false} bind:checked={$prefs.swatchA11y} on:message={() => togglePrefs('swatchA11y')} />
					{#if $prefs.swatchA11y}
						<InputCheckbox label="Large font" name={'large-font'} disabled={false} bind:checked={$prefs.swatchA11yLarge} on:message={() => updateA11yFontSize()} />
						<div class="options-group">
							<Input label="Text Color 1" bind:value={$prefs.textColors[0].hex} name="text-1" placeholder="000000" on:message={() => {updateA11yThemePrefs(0)}} maxChars={6} warning={$prefs.textColors[0].valid || $prefs.textColors[0].hex === '' ? '' : a11yValidityWarning} />
							<Input label="Text Color 2" bind:value={$prefs.textColors[1].hex} name="text-2" placeholder="000000" on:message={() => {updateA11yThemePrefs(1)}} maxChars={6} warning={$prefs.textColors[1].valid || $prefs.textColors[1].hex === '' ? '' : a11yValidityWarning} />
							<Input label="Text Color 3" bind:value={$prefs.textColors[2].hex} name="text-3" placeholder="000000" on:message={() => {updateA11yThemePrefs(2)}} maxChars={6} warning={$prefs.textColors[2].valid || $prefs.textColors[2].hex === '' ? '' : a11yValidityWarning} />
							<Input label="Text Color 4" bind:value={$prefs.textColors[3].hex} name="text-4" placeholder="000000" on:message={() => {updateA11yThemePrefs(3)}} maxChars={6} warning={$prefs.textColors[3].valid || $prefs.textColors[3].hex === '' ? '' : a11yValidityWarning} />
						</div>
						<Callout>
							<p>Learn about text accessibility and color contrast ratios on <a href="https://developer.mozilla.org/en-US/docs/Web/Accessibility/Understanding_WCAG/Perceivable/Color_contrast">MDN Web docs</a>.</p>
						</Callout>
					{/if}
				</div>
			</div>
		</div>
	</section>
	

	<section class="sm-order-0 section-families">
		<div class="families-area" class:active={$appState.familySelected || $appState.global != -1}>
			{#each $familiesS as family}
				<ColorFamily id={family.id} functions={familyFunctions} selected={$appState.family === family.id && $appState.familySelected} />
			{/each}
		</div>
		{#if !$appState.familySelected && $appState.global === -1 || $windowWidth > 960}
			<div class="add-family-button-area" transition:slide>
				<Button label="Add Family" handleClick={() => addFamily(addFamilyHex)} role="secondary" icon="plus" />
			</div>
		{/if}

	</section>
	

	<section class="family-controls-area">	
		<FamilyControls id={$appState.family} functions={familyFunctions} />
	</section>
</main>

{#if $appState.modalOpen || $appState.view !== 0}
	<Backdrop />
{/if}

{#if $appState.view === 2}
	<FullView>
		<div class="text-block">
			<h1>Export</h1>
			<div class="tab-area-small" role="tablist">
				<TabIcon label="SVG" selected={$prefs.exportCodeType === 'SVG'} handleClick={() => {updatePrefs('exportCodeType', 'SVG')}} tabid={'global-tab-0'} tabStyleUnderlined={true} largeLabel/>
				<TabIcon label="CSS" selected={$prefs.exportCodeType === 'CSS'} handleClick={() => {updatePrefs('exportCodeType', 'CSS')}} tabid={'global-tab-1'} tabStyleUnderlined={true} largeLabel/>
				<TabIcon label="Design Tokens" selected={$prefs.exportCodeType === 'Design Tokens'} handleClick={() => {updatePrefs('exportCodeType', 'Design Tokens')}} tabid={'global-tab-2'} tabStyleUnderlined={true} largeLabel/>
			</div>
			{#if $prefs.exportCodeType === 'SVG'}
				<Code code={exportCode.svg}/>
				<Callout>
					<p><b>Tip: Quickest way to Figma et al.</b> ---> copy and paste this SVG data directly into your design tool of choice. Et voila!</p>
				</Callout>
			{:else if $prefs.exportCodeType === 'CSS'}
				<Code code={exportCode.css}/>
			{:else if $prefs.exportCodeType === 'Design Tokens'}
				<Code code={exportCode.tokens}/>
				<Callout icon="alert">
					<p>The <a href="https://tr.designtokens.org/format/">design token format</a> is still in a draft state as of October 2023.</p>
				</Callout>
			{/if}
			<HorizontalRule />
			<h2>Options</h2>
			<InputCheckbox label="Include accessiblity data" name="export-accessibility-pref" bind:checked={$prefs.exportAccessibility} on:message={() => {exportCode = writeExportCode($familiesS, $lightnessParamsS, $prefs);}} />
			<h3>Color format</h3>
			{#if $prefs.exportCodeType !== 'Design Tokens'}
				<InputRadio name={'css-color-type'} options={['Hex', 'HSL', 'RGB']} bind:value={$prefs.exportColorFormat} on:message={() => {exportCode = writeExportCode($familiesS, $lightnessParamsS, $prefs);}} />
			{:else}
				<Callout>
					<p>The design token spec <a href="https://tr.designtokens.org/format/#color">requires</a> colors to be specified in hexidecimal format.</p>
				</Callout>
			{/if}

		</div>
	</FullView>
{/if}
{#if $appState.view === 3}
	<FullView>
		<div class="text-block">
			<BrandButton large />
			<h1>About Color Pal</h1>
			<p>Color Pal is a tool for selecting color palettes for design systems. </p>
			<h2>Made by Kieffer Bros.</h2>
			<p>If you have feedback, we'd love to hear it. Send us an <a href="mailto:hello@kiefferbros.com">email</a>. In another life we made <a href="https://kiefferbros.com">puzzles games</a>.</p>
			<h2>Acknowledgements</h2>
			<p>This website was built with <a href="https://svelte.dev/">Svelte</a>, an open source, user interface framework. The font used on this website is <a href="https://rsms.me/inter/">Inter</a>, which is open source and licensed under the SIL Open Font License 1.1. We used the <a href="https://github.com/material-foundation/material-color-utilities">Material Color Utilities</a>, licensed under Apache 2.0 License, to include the HCT color space.</p>
			<h2>Disclosures</h2>
			<p>I'm using <a href="https://plausible.io">Plausible.io</a> to count how may people visit this site. This site is hosted by Digital Ocean using their <a href="https://www.digitalocean.com/products/app-platform">App Platform</a>. I am also using a <a href="https://rsms.me/inter/inter.css">CDN</a> provided Rasmus Andersson to serve up the fonts.</p>
			<HorizontalRule />
			<p>&#169; 2023 Kieffer Bros., LLC</p>
		</div>
	</FullView>
{/if}
{#if $appState.view === 4}
	<FullView>
		<div class="text-block">
			<h1>Settings</h1>
			<h2>Color Space</h2>
			<InputRadio name={'color-space'} options={['HSL', 'HCT']} bind:value={$prefs.colorSpace} on:message={() => udpateColorSpace($prefs.colorSpace)} />
			<Callout>
				<p><a href="https://material.io/blog/science-of-color-design">HCT</a> is a newer (2021) color space that is functionally similar to HSL, but is designed to be more consistent across the color spectrum as humans perceive tone/brightness/lightness.</p>
			</Callout>
			<h2>Theme</h2>
			<InputRadio name={'site-theme'} options={['System', 'White', 'Black', 'Dark gray']} bind:value={$prefs.theme} on:message={() => {manageTheme($prefs.theme)}} />
		</div>
	</FullView>
{/if}

<!-- Modal comes last so that it can get rendered on top -->
{#if $appState.modalOpen}
<Modal open={$appState.modalOpen}>
	<h3>Delete {$familiesS[getFamilyIndex($appState.family)].name}?</h3>
	<div class="modal-button-group">
		<Button label={'Cancel'} handleClick={() => {$appState.modalOpen = false}} role="secondary"/>
		<Button label={'Delete'} handleClick={() => {deleteFamily($appState.family), $appState.modalOpen = false}} role="destructive" />
	</div>
</Modal>
{/if}

<style>
	:global(:root) {
		/* Gray */
		--gray-0: #000000;
		--gray-1: #040404;
		--gray-6: #0e0e0e;
		--gray-13: #202020;
		--gray-22: #393939;
		--gray-35: #595959;
		--gray-50: #808080;
		--gray-65: #a6a6a6;
		--gray-78: #c6c6c6;
		--gray-88: #dfdfdf;
		--gray-94: #f1f1f1;
		--gray-99: #fbfbfb;
		--gray-100: #ffffff;

		/* Theme 1 */
		--background: var(--gray-100);
		--background-offset: var(--gray-88);
		--background-secondary: var(--gray-94);
		--browser-accent: var(--gray-50);
		--text: var(--gray-0);
		--text-disabled: var(--gray-35);
		--action: var(--gray-0);
		--alert: #e85d21;
		--bg-cover: rgba(255,255,255,0.75);
	}
	/* Dark mode */
	@media (prefers-color-scheme: dark) {
	:global(:root) {
            --background: var(--gray-0);
            --background-offset: var(--gray-22);
            --background-secondary: var(--gray-6);
            --browser-accent: var(--gray-50);
            --text: var(--gray-100);
			--text-disabled: var(--gray-65);
            --action: var(--gray-100);
			--alert: #e85d21;
			--bg-cover: rgba(0,0,0,0.75);
        }
    }
	:global(html[data-theme="White"]) {
		--background: var(--gray-100);
		--background-offset: var(--gray-88);
		--background-secondary: var(--gray-94);
		--browser-accent: var(--gray-50);
		--text: var(--gray-0);
		--text-disabled: var(--gray-35);
		--action: var(--gray-0);
		--alert: #e85d21;
		--bg-cover: rgba(255,255,255,0.75);
	}
	:global(html[data-theme="Black"]) {
		--background: var(--gray-0);
		--background-offset: var(--gray-22);
		--background-secondary: var(--gray-6);
		--browser-accent: var(--gray-50);
		--text: var(--gray-100);
		--text-disabled: var(--gray-65);
		--action: var(--gray-100);
		--alert: #e85d21;
		--bg-cover: rgba(0,0,0,0.75);
	}
	:global(html[data-theme="Dark gray"]) {
		--background: var(--gray-13);
		--background-offset: var(--gray-35);
		--background-secondary: var(--gray-22);
		--browser-accent: var(--gray-50);
		--text: var(--gray-88);
		--text-disabled: var(--gray-65);
		--action: var(--gray-88);
		--alert: #e85d21;
		--bg-cover: rgba(0,0,0,0.75);
	}

	:global(body) {
		font-family: 'Inter', sans-serif ;
		margin: 0;
		padding: 0;
		position: absolute;
		top: 0;
		width: 100vw;
		height: 100vh;
		display: grid;
		grid-template-rows: 56px auto;
		accent-color: var(--browser-accent);
		background-color: var(--background);
		color: var(--text);
	}

	:global(.button-area) {
		margin: 2rem 0;
		display: flex;
		align-content: flex-end;
	}

	:global(.button-area > Button:not(:last-child)) {
		margin-right: 2rem;
	}
	:global(.fine-print) {
		font-size: smaller;
		line-height: 1rem;
	}
	h1 {
		font-size: 3rem;
	}
	header {
		margin: 0;
		display: flex;
		border-bottom: 1px solid var(--background-offset);
	}
	.site-brand-area {
		display: flex;
		align-items: center;
		padding: 0 1rem;
	}
	h1.title-text {
		font-size: smaller;
		margin-right: 0.75rem;
	}
	main {
		display: grid;
		grid-template-columns: 80px auto 0;
		grid-template-rows: 100%;
		margin: 0;
		transition: 0.15s;
		height: calc(100vh - 56px); /* full screen height minus the header*/
	}
	main.global-active {
		grid-template-columns: 480px auto 0;
	}
	main.family-selected {
		grid-template-columns: 80px auto 300px;
	}
	main.family-selected.global-active {
		grid-template-columns: 380px auto 300px;
	}
	.tab-area {
		border-right: 1px solid var(--background-offset);
	}
	.tab-area-small {
		display:grid;
		grid-template-columns: 1fr 1fr 1fr;
		padding: 0 0 2rem 0;
		border-right: none;
	}
	.section-families {
		overflow-y: scroll;
		overflow-x: clip;
	}
	.families-area {
		min-height: calc(100% - 128px);
		display: grid;
	}
	.add-family-button-area {
		padding: 1rem 0.5rem;
		text-align: center;
		height: 56px;
	}
	.info-area {
		height: 100%;
		display: none;
    }
	.info-area.active {
		display: block;
	}
	.family-controls-area {
		overflow: hidden;
		/* border-left: 1px solid var(--background-offset); */
	}
	.info-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}
	.info-header > h2 {
		margin: 0;
		padding-left: 1rem;
	}
	.info-content {
		overflow: hidden;
		height: calc(100% - 56px);
		overflow-y: scroll;
		padding: 0 1rem;
	}
	.global-settings-area {
		display: grid;
		grid-template-columns: 80px calc(100% - 80px);
		grid-template-rows: 100%;
		height: 100%;
	}
	.global-settings-area.active .settings-area {
		border-right: 1px solid var(--background-offset);
	}
	.options-group {
		margin: 1rem 0;
	}
	.text-block {
		max-width: 520px;
		padding: 1rem;
		margin: 0 auto 2rem;
		line-height: 1.5;;
	}
	.text-block h1 {
		margin-bottom: 1rem;
	}
	.text-block h2 {
		margin-bottom: 0.5rem;
	}
	.text-block p {
		margin: 0.5rem 0;
	}
	:global(.small-only) {
		display: none;
	}

	@media (max-width: 960px) {
		:global(body) {
			grid-template-rows: 56px auto;
		}
		header {
			margin: 0 0 0 0.5rem;
			justify-content: space-between;
		}
		main {
			display: grid;
			grid-template-columns: 100%;
			grid-template-rows: calc(100% - 64px) auto auto;
			margin: 0;
		}
		main.global-active {
			grid-template-columns: 100%;
			grid-template-rows: 25% 0 75%;
		}
		main.family-selected {
			grid-template-columns: 100%;
			grid-template-rows: auto 0 0;
		}
		main.family-selected.global-active {
			grid-template-columns: 100%;
			grid-template-rows: 25vh 0 0;
		}
		.global-settings-area {
			background: var(--background);
			grid-template-columns: auto;
			grid-template-rows: 64px 0;
			box-sizing: border-box;
			z-index: 2;
			overflow: hidden;
		}
		.global-settings-area.active {
			grid-template-columns: auto;
			grid-template-rows: 100% 64px;
		}
		.tab-area {
			display:grid;
			grid-template-columns: 1fr 1fr 1fr;
			padding: 0;
			border-right: none;
		}
		.settings-area {
			overflow: hidden;
		}
		.global-settings-area.active .settings-area {
			overflow-y: scroll;
			order: -1;
		}
		.families-area {
			min-height: calc(100% - 88px);
			transition: 0.3s;
		}
		.families-area.active {
			min-height: 25vh;
			height: 25vh;
		}
		.info-area {
			width: 100%;
			display: none;
			box-sizing: border-box;
		}
		.info-area.active {
			display: block;
		}
		.sm-order-0 {
			order: 0;
		}
		.sm-order-1 {
			order: 1;
		}
		:global(.small-only) {
			display: block;
		}
	}
</style>